import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { FinancialPOAIntake } from '@bedside-docs/contracts';
import {
  getFinancialPOADocumentContext,
  isFetchFinancialPOADocumentContextError,
  previewShareEmail,
  sendShares,
  type SendSharesPayload,
} from '../../lib/api';
import { useFinancialPoaFormContext } from './FinancialPoaFormContext';
import { parseFinancialPoaIntakeCandidate } from './financialIntakeAdapter';
import styles from './SharePOA.module.scss';

type StoredResult = {
  id: string;
  pdfUrl: string;
  expiresAt: number;
};

type RecipientRole = SendSharesPayload['recipients'][number]['role'];

type RecipientResult = {
  email: string;
  status: 'SENT' | 'FAILED';
  error?: string;
  role: RecipientRole;
  name?: string;
};

type EmailSuggestion = {
  email: string;
  name?: string;
};

const LAST_RESULT_STORAGE_KEY = 'poa.financial.last.v1';
const DRAFT_STORAGE_KEYS = ['poa.financial.draft.v2', 'poa.financial.draft.v1'];

const MAX_MESSAGE_LENGTH = 500;

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const getStoredIntake = (): FinancialPOAIntake | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  for (const key of DRAFT_STORAGE_KEYS) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        continue;
      }

      const parsed = JSON.parse(raw) as unknown;
      const result = parseFinancialPoaIntakeCandidate(parsed);
      if (result.success) {
        return result.data;
      }
    } catch (error) {
      console.warn('Failed to parse stored intake', error);
    }
  }

  return null;
};

const getStoredResult = (): StoredResult | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(LAST_RESULT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'id' in parsed &&
      'pdfUrl' in parsed &&
      'expiresAt' in parsed
    ) {
      const candidate = parsed as StoredResult;
      if (
        typeof candidate.id === 'string' &&
        typeof candidate.pdfUrl === 'string' &&
        typeof candidate.expiresAt === 'number'
      ) {
        return candidate;
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored financial POA result metadata', error);
  }

  return null;
};

const persistStoredResult = (result: StoredResult) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      LAST_RESULT_STORAGE_KEY,
      JSON.stringify(result),
    );
  } catch (error) {
    console.warn('Failed to store financial POA result metadata', error);
  }
};

const clearStoredResult = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(LAST_RESULT_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear stored financial POA result metadata', error);
  }
};

const FRESHNESS_BUFFER_SECONDS = 30;

const uniqueRecipients = (
  recipients: SendSharesPayload['recipients'],
): SendSharesPayload['recipients'] => {
  const seen = new Set<string>();
  const deduped: SendSharesPayload['recipients'] = [];

  for (const recipient of recipients) {
    const key = normalizeEmail(recipient.email);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    deduped.push(recipient);
  }

  return deduped;
};

const disablePreviewLinks = (html: string): string => {
  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(html, 'text/html');

    const disableStyle = parsedDocument.createElement('style');
    disableStyle.textContent = `
      a[data-preview-link-disabled] {
        pointer-events: none !important;
        cursor: default !important;
      }

      a[data-preview-link-disabled]:focus {
        outline: none !important;
      }
    `;

    parsedDocument.querySelectorAll('a').forEach((anchor) => {
      anchor.setAttribute('data-preview-link-disabled', 'true');
      anchor.setAttribute('tabindex', '-1');
      anchor.setAttribute('aria-disabled', 'true');
      if (!anchor.getAttribute('role')) {
        anchor.setAttribute('role', 'link');
      }
    });

    parsedDocument.head?.append(disableStyle);

    return parsedDocument.documentElement.outerHTML;
  } catch (error) {
    console.warn('Failed to disable links in preview HTML', error);
    return html;
  }
};

const SharePOA = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formContext = useFinancialPoaFormContext();

  const [docContext, setDocContext] = useState<StoredResult | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [principalSelected, setPrincipalSelected] = useState(false);
  const [agentSelected, setAgentSelected] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ recipients?: string; email?: string; message?: string }>(
    {},
  );
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [sendResults, setSendResults] = useState<RecipientResult[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [retryingEmail, setRetryingEmail] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const recipientsRef = useRef<HTMLFieldSetElement | null>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement | null>(null);
  const previewCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);

  const docId = searchParams.get('id')?.trim() ?? '';

  useEffect(() => {
    if (!docId) {
      setDocContext(null);
      setDocError('Document details are missing.');
      return;
    }

    const stored = getStoredResult();
    const nowSeconds = Math.floor(Date.now() / 1000);
    const isStoredFresh =
      Boolean(stored) &&
      stored?.id === docId &&
      stored.expiresAt > nowSeconds + FRESHNESS_BUFFER_SECONDS;

    if (isStoredFresh && stored) {
      setDocContext(stored);
      setDocError(null);
      return;
    }

    if (stored?.id !== docId && stored) {
      clearStoredResult();
    }

    setDocContext(null);
    setDocError(null);

    let isCancelled = false;

    void (async () => {
      try {
        const result = await getFinancialPOADocumentContext(docId);
        if (isCancelled) {
          return;
        }

        setDocContext(result);
        setDocError(null);
        persistStoredResult(result);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        if (
          isFetchFinancialPOADocumentContextError(error) &&
          error.type === 'not_found'
        ) {
          clearStoredResult();
          setDocContext(null);
          setDocError('We could not find the document details to share.');
        } else if (
          isFetchFinancialPOADocumentContextError(error) &&
          error.type === 'server_error' &&
          error.message
        ) {
          setDocError(error.message);
        } else {
          setDocError('We could not refresh the document link. Please try again.');
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [docId]);

  const intake = useMemo<FinancialPOAIntake | null>(() => {
    if (formContext?.intake) {
      return formContext.intake;
    }

    return getStoredIntake();
  }, [formContext]);

  const principalEmail = intake?.principal.email?.trim() ?? '';
  const principalName = intake?.principal.fullName;
  const agentEmail = intake?.agent.email?.trim() ?? '';
  const agentName = intake?.agent.fullName;

  const successorSuggestions = useMemo<EmailSuggestion[]>(() => {
    if (!intake?.successorAgents?.length) {
      return [];
    }

    const suggestions: EmailSuggestion[] = [];
    const seen = new Set<string>();

    for (const successor of intake.successorAgents) {
      const trimmedEmail = successor.email?.trim();
      if (!trimmedEmail) {
        continue;
      }

      const normalized = normalizeEmail(trimmedEmail);
      if (seen.has(normalized)) {
        continue;
      }

      seen.add(normalized);

      const trimmedName = successor.fullName?.trim();
      suggestions.push({
        email: trimmedEmail,
        name: trimmedName ? trimmedName : undefined,
      });
    }

    return suggestions;
  }, [intake?.successorAgents]);

  const principalInitialized = useRef(false);
  const agentInitialized = useRef(false);

  useEffect(() => {
    if (principalInitialized.current) {
      return;
    }

    if (principalEmail) {
      setPrincipalSelected(true);
    }

    principalInitialized.current = true;
  }, [principalEmail]);

  useEffect(() => {
    if (agentInitialized.current) {
      return;
    }

    if (agentEmail) {
      setAgentSelected(true);
    }

    agentInitialized.current = true;
  }, [agentEmail]);

  const totalRecipientsCount =
    additionalEmails.length +
    (principalSelected && principalEmail ? 1 : 0) +
    (agentSelected && agentEmail ? 1 : 0);

  const isEmailAlreadyIncluded = useCallback(
    (email: string) => {
      const normalized = normalizeEmail(email);

      if (principalEmail && normalizeEmail(principalEmail) === normalized) {
        return true;
      }

      if (agentEmail && normalizeEmail(agentEmail) === normalized) {
        return true;
      }

      return additionalEmails.some(
        (value) => normalizeEmail(value) === normalized,
      );
    },
    [additionalEmails, agentEmail, principalEmail],
  );

  const canAddMoreRecipients = totalRecipientsCount < 5;

  const resetEmailErrors = () => {
    setErrors((prev) => ({ ...prev, email: undefined }));
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.target.value);
    if (errors.email) {
      resetEmailErrors();
    }
  };

  const removeAdditionalEmail = (email: string) => {
    setAdditionalEmails((previous) => previous.filter((value) => value !== email));
  };

  const handleBackspaceRemove = () => {
    if (additionalEmails.length === 0) {
      return;
    }

    const lastEmail = additionalEmails[additionalEmails.length - 1];
    removeAdditionalEmail(lastEmail);
  };

  const handleAddEmail = (rawEmail?: string) => {
    const value = rawEmail ?? emailInput;
    const trimmed = value.trim().replace(/[,\s]+$/, '');
    if (!trimmed) {
      if (!rawEmail) {
        setEmailInput('');
      }
      return;
    }

    if (isEmailAlreadyIncluded(trimmed)) {
      setErrors((prev) => ({
        ...prev,
        email: 'That email address is already included.',
      }));
      return;
    }

    if (!canAddMoreRecipients) {
      setErrors((prev) => ({
        ...prev,
        email: 'You can send the document to at most five recipients.',
      }));
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmed)) {
      setErrors((prev) => ({
        ...prev,
        email: 'Enter a valid email address before adding it.',
      }));
      return;
    }

    setAdditionalEmails((previous) => [...previous, trimmed]);
    setErrors((prev) => ({
      ...prev,
      recipients: undefined,
      email: undefined,
    }));
    if (!rawEmail) {
      setEmailInput('');
    }
  };

  const handleEmailKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleAddEmail();
      return;
    }

    if (event.key === 'Backspace' && emailInput === '') {
      handleBackspaceRemove();
    }
  };

  const handleEmailBlur = () => {
    handleAddEmail();
  };

  const buildRecipients = useCallback((): SendSharesPayload['recipients'] => {
    const recipients: SendSharesPayload['recipients'] = [];

    if (principalSelected && principalEmail) {
      recipients.push({ role: 'principal', email: principalEmail, name: principalName });
    }

    if (agentSelected && agentEmail) {
      recipients.push({ role: 'agent', email: agentEmail, name: agentName });
    }

    for (const email of additionalEmails) {
      recipients.push({ role: 'other', email });
    }

    return uniqueRecipients(recipients);
  }, [
    additionalEmails,
    agentEmail,
    agentName,
    agentSelected,
    principalEmail,
    principalName,
    principalSelected,
  ]);

  const previewRecipients = useMemo(() => buildRecipients(), [buildRecipients]);

  useEffect(() => {
    if (isPreviewOpen) {
      previewCloseButtonRef.current?.focus();
    }
  }, [isPreviewOpen]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsPreviewOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPreviewOpen]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return;
    }

    const { body } = document;
    if (!body) {
      return;
    }

    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isPreviewOpen]);

  useEffect(() => {
    if (!isPreviewOpen) {
      setIsPreviewLoading(false);
      setPreviewHtml(null);
      setPreviewError(null);
      if (previewFrameRef.current) {
        previewFrameRef.current.style.height = '0px';
      }
      return;
    }

    if (!docContext) {
      setIsPreviewLoading(false);
      setPreviewHtml(null);
      setPreviewError('We need the document details before we can show the email preview.');
      return;
    }

    const recipients = buildRecipients();
    if (recipients.length === 0) {
      setIsPreviewLoading(false);
      setPreviewHtml(null);
      setPreviewError('Add at least one recipient to preview the email.');
      return;
    }

    const trimmedMessage = message.trim();
    setIsPreviewLoading(true);
    setPreviewHtml(null);
    setPreviewError(null);

    if (previewFrameRef.current) {
      previewFrameRef.current.style.height = '0px';
    }

    let isCancelled = false;

    void (async () => {
      try {
        const result = await previewShareEmail({
          docId: docContext.id,
          url: docContext.pdfUrl,
          recipients,
          message: trimmedMessage ? trimmedMessage : undefined,
        });

        if (isCancelled) {
          return;
        }

        setPreviewHtml(disablePreviewLinks(result.html));
      } catch (error) {
        if (isCancelled) {
          return;
        }

        const fallback =
          error instanceof Error
            ? error.message
            : 'Failed to load the email preview.';
        setPreviewError(fallback);
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    buildRecipients,
    docContext,
    isPreviewOpen,
    message,
  ]);

  const handlePreviewFrameLoad = useCallback(() => {
    const iframe = previewFrameRef.current;
    if (!iframe) {
      return;
    }

    try {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }

      const body = doc.body;
      if (!body) {
        return;
      }

      const height = Math.max(body.scrollHeight, 400);
      iframe.style.height = `${height}px`;
    } catch (error) {
      console.warn('Failed to size email preview frame', error);
    }
  }, []);

  const focusFirstError = () => {
    const fieldOrder: Array<HTMLElement | null> = [
      alertRef.current,
      (recipientsRef.current?.querySelector('input') as HTMLElement | null) ?? null,
      emailInputRef.current,
      messageRef.current,
      copyButtonRef.current,
    ];

    for (const field of fieldOrder) {
      if (field) {
        field.focus();
        break;
      }
    }
  };

  const handleSend = async () => {
    setStatusMessage(null);
    setErrors({});
    setCopyStatus('idle');

    if (!docContext) {
      setDocError('We need the document details before we can send emails.');
      focusFirstError();
      return;
    }

    const recipients = buildRecipients();

    if (recipients.length === 0) {
      setErrors({ recipients: 'Choose at least one recipient to continue.' });
      focusFirstError();
      return;
    }

    if (recipients.length > 5) {
      setErrors({ recipients: 'You can send the document to at most five recipients.' });
      focusFirstError();
      return;
    }

    const trimmedMessage = message.trim();
    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setErrors({ message: 'Keep your message under 500 characters.' });
      focusFirstError();
      return;
    }

    setIsSending(true);
    try {
      const response = await sendShares({
        docId: docContext.id,
        url: docContext.pdfUrl,
        recipients,
        message: trimmedMessage ? trimmedMessage : undefined,
      });

      const mappedResults: RecipientResult[] = response.results.map((result) => {
        const role =
          recipients.find(
            (recipient) => normalizeEmail(recipient.email) === normalizeEmail(result.email),
          )?.role ?? 'other';

        const name =
          recipients.find(
            (recipient) => normalizeEmail(recipient.email) === normalizeEmail(result.email),
          )?.name ?? undefined;

        return {
          email: result.email,
          status: result.status,
          error: result.error,
          role,
          name,
        };
      });

      setSendResults(mappedResults);
      setStatusMessage('Your document is on its way!');
      setDocError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send emails.';
      setStatusMessage(message);
      setSendResults([]);
    } finally {
      setIsSending(false);
    }
  };

  const handleRetry = async (recipient: RecipientResult) => {
    if (!docContext) {
      setDocError('We need the document details before we can send emails.');
      focusFirstError();
      return;
    }

    setRetryingEmail(recipient.email);
    setStatusMessage(null);

    try {
      const response = await sendShares({
        docId: docContext.id,
        url: docContext.pdfUrl,
        recipients: [
          {
            role: recipient.role,
            email: recipient.email,
            name: recipient.name,
          },
        ],
        message: message.trim() ? message.trim() : undefined,
      });

      const result = response.results[0];
      setSendResults((previous) =>
        previous.map((entry) =>
          normalizeEmail(entry.email) === normalizeEmail(result.email)
            ? { ...entry, status: result.status, error: result.error }
            : entry,
        ),
      );

      if (result.status === 'SENT') {
        setStatusMessage(`We re-sent the document to ${recipient.email}.`);
      } else if (result.error) {
        setStatusMessage(result.error);
      }
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Failed to resend email.';
      setStatusMessage(messageText);
    } finally {
      setRetryingEmail(null);
    }
  };

  const handleSkip = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/poa/financial/review');
    }
  };

  const handleCopyLink = async () => {
    if (!docContext) {
      setDocError('We need a document link before we can copy it.');
      focusFirstError();
      return;
    }

    if (!navigator?.clipboard) {
      setCopyStatus('error');
      setStatusMessage('Copy is not supported in this browser.');
      return;
    }

    try {
      await navigator.clipboard.writeText(docContext.pdfUrl);
      setCopyStatus('success');
      setStatusMessage('Link copied to your clipboard.');
    } catch (error) {
      console.warn('Failed to copy document link', error);
      setCopyStatus('error');
      setStatusMessage('We could not copy the link. Try manually copying from your browser.');
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Share your document</h1>
            <p className={styles.subtitle}>
              Email the PDF attachment to the people who need this document.
            </p>
          </div>
        </header>

        {docError && (
          <div className={styles.alert} role="alert" ref={alertRef} tabIndex={-1}>
            <p>{docError}</p>
          </div>
        )}

        {statusMessage && (
          <div className={styles.status} role="status" aria-live="polite">
            {statusMessage}
          </div>
        )}

        <section className={styles.card}>
          <fieldset className={styles.recipients} ref={recipientsRef}>
            <legend>Send to</legend>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={principalSelected && Boolean(principalEmail)}
                  onChange={(event) => {
                    setPrincipalSelected(event.target.checked);
                    if (errors.recipients) {
                      setErrors((prev) => ({ ...prev, recipients: undefined }));
                    }
                  }}
                  disabled={!principalEmail}
                />
                <span>
                  Principal
                  {principalName && <Fragment> — {principalName}</Fragment>}
                  {principalEmail ? (
                    <span className={styles.emailValue}>{principalEmail}</span>
                  ) : (
                    <span className={styles.missingEmail}>Email not provided</span>
                  )}
                </span>
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={agentSelected && Boolean(agentEmail)}
                  onChange={(event) => {
                    setAgentSelected(event.target.checked);
                    if (errors.recipients) {
                      setErrors((prev) => ({ ...prev, recipients: undefined }));
                    }
                  }}
                  disabled={!agentEmail}
                />
                <span>
                  Agent
                  {agentName && <Fragment> — {agentName}</Fragment>}
                  {agentEmail ? (
                    <span className={styles.emailValue}>{agentEmail}</span>
                  ) : (
                    <span className={styles.missingEmail}>Email not provided</span>
                  )}
                </span>
              </label>
            </div>
            {errors.recipients && (
              <p className={styles.error} role="alert">
                {errors.recipients}
              </p>
            )}
          </fieldset>

          <div className={styles.emailInputGroup}>
            <label htmlFor="share-additional-email">Add more recipients</label>
            <div className={styles.emailInputField}>
              {additionalEmails.map((email) => (
                <span key={email} className={styles.chip}>
                  {email}
                  <button
                    type="button"
                    onClick={() => removeAdditionalEmail(email)}
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                id="share-additional-email"
                ref={emailInputRef}
                type="email"
                value={emailInput}
                onChange={handleEmailChange}
                onKeyDown={handleEmailKeyDown}
                onBlur={handleEmailBlur}
                placeholder="Type an email and press Enter"
                aria-invalid={errors.email ? 'true' : 'false'}
              />
            </div>
            {errors.email && (
              <p className={styles.error} role="alert">
                {errors.email}
              </p>
            )}
          </div>

          {successorSuggestions.length > 0 && (
            <div className={styles.suggestions}>
              <p className={styles.suggestionsLabel}>Suggested recipients</p>
              <ul className={styles.suggestionsList}>
                {successorSuggestions.map((suggestion) => {
                  const disabled =
                    !canAddMoreRecipients ||
                    isEmailAlreadyIncluded(suggestion.email);
                  const displayName = suggestion.name ?? 'Successor agent';

                  return (
                    <li key={suggestion.email}>
                      <button
                        type="button"
                        className={styles.suggestionButton}
                        onClick={() => handleAddEmail(suggestion.email)}
                        disabled={disabled}
                        aria-label={`Add ${displayName} (${suggestion.email}) to recipients`}
                      >
                        <span className={styles.suggestionName}>{displayName}</span>
                        <span className={styles.suggestionEmail}>{suggestion.email}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className={styles.messageGroup}>
            <label htmlFor="share-message">
              Add a personal note <span className={styles.optional}>(optional)</span>
            </label>
            <textarea
              id="share-message"
              ref={messageRef}
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (errors.message) {
                  setErrors((prev) => ({ ...prev, message: undefined }));
                }
              }}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={5}
              aria-invalid={errors.message ? 'true' : 'false'}
            />
            <div className={styles.messageMeta}>
              <span>{MAX_MESSAGE_LENGTH - message.length} characters left</span>
            </div>
            {errors.message && (
              <p className={styles.error} role="alert">
                {errors.message}
              </p>
            )}
          </div>
        </section>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSend}
            disabled={isSending}
            aria-busy={isSending}
          >
            {isSending && <span className={styles.spinner} aria-hidden="true" />}
            <span>{isSending ? 'Sending…' : 'Send now'}</span>
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setIsPreviewOpen(true)}
            disabled={!docContext}
          >
            Preview email
          </button>
          <button type="button" className={styles.tertiaryButton} onClick={handleSkip}>
            Skip for now
          </button>
        </div>

        <div className={styles.copyGroup}>
          <button
            type="button"
            className={styles.copyButton}
            onClick={handleCopyLink}
            ref={copyButtonRef}
          >
            Copy link
          </button>
          <span className={styles.copyStatus} aria-live="polite">
            {copyStatus === 'success' && 'Copied!'}
            {copyStatus === 'error' && 'Copy failed'}
          </span>
        </div>

        {sendResults.length > 0 && (
          <section className={styles.results}>
            <h2>Delivery status</h2>
            <ul>
              {sendResults.map((result) => (
                <li key={result.email} className={styles.resultItem}>
                  <div>
                    <p className={styles.resultEmail}>{result.email}</p>
                    <p className={styles.resultMeta}>
                      {result.role === 'principal' && 'Principal'}
                      {result.role === 'agent' && 'Agent'}
                      {result.role === 'other' && 'Additional recipient'}
                    </p>
                  </div>
                  <div className={styles.resultStatusWrapper}>
                    <span
                      className={
                        result.status === 'SENT'
                          ? styles.statusSent
                          : styles.statusFailed
                      }
                    >
                      {result.status === 'SENT' ? 'Sent' : 'Failed'}
                    </span>
                    {result.status === 'FAILED' && (
                      <button
                        type="button"
                        onClick={() => handleRetry(result)}
                        disabled={retryingEmail === result.email}
                        className={styles.retryButton}
                      >
                        {retryingEmail === result.email ? 'Retrying…' : 'Retry'}
                      </button>
                    )}
                    {result.error && (
                      <p className={styles.resultError}>{result.error}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        {isPreviewOpen && docContext && (
          <div
            className={styles.modalBackdrop}
            role="presentation"
            onClick={() => setIsPreviewOpen(false)}
          >
            <div
              className={styles.modalDialog}
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-preview-title"
              aria-describedby="email-preview-description"
              onClick={(event) => event.stopPropagation()}
            >
              <header className={styles.modalHeader}>
                <h2 className={styles.modalTitle} id="email-preview-title">
                  Preview email
                </h2>
                <button
                  type="button"
                  className={styles.modalCloseButton}
                  onClick={() => setIsPreviewOpen(false)}
                  ref={previewCloseButtonRef}
                  aria-label="Close preview"
                >
                  ×
                </button>
              </header>
              <div className={styles.modalBody} id="email-preview-description">
                <section className={styles.previewMeta}>
                  <div className={styles.previewMetaGroup}>
                    <span className={styles.previewMetaLabel}>Subject</span>
                    <span className={styles.previewMetaValue}>
                      Your BedsideDocs document is ready
                    </span>
                  </div>
                  <div className={styles.previewMetaGroup}>
                    <span className={styles.previewMetaLabel}>Recipients</span>
                    {previewRecipients.length > 0 ? (
                      <ul className={styles.previewRecipientsList}>
                        {previewRecipients.map((recipient) => (
                          <li key={recipient.email}>
                            <span className={styles.previewRecipientEmail}>{recipient.email}</span>
                            <span className={styles.previewRecipientRole}>
                              {recipient.role === 'principal' && 'Principal'}
                              {recipient.role === 'agent' && 'Agent'}
                              {recipient.role === 'other' && 'Additional recipient'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.previewRecipientsEmpty}>
                        Add at least one recipient to see who will receive this email.
                      </p>
                    )}
                  </div>
                </section>
                <section className={styles.previewEmail}>
                  {isPreviewLoading && (
                    <div className={styles.previewLoading} role="status" aria-live="polite">
                      <span className={styles.previewLoadingSpinner} aria-hidden="true" />
                      <span className={styles.previewLoadingText}>Loading preview…</span>
                    </div>
                  )}
                  {!isPreviewLoading && previewError && (
                    <p className={styles.previewError}>{previewError}</p>
                  )}
                  {!isPreviewLoading && !previewError && previewHtml && (
                    <div className={styles.previewEmailFrame}>
                      <iframe
                        ref={previewFrameRef}
                        className={styles.previewIframe}
                        title="Share email preview"
                        srcDoc={previewHtml}
                        sandbox="allow-same-origin"
                        scrolling="no"
                        onLoad={handlePreviewFrameLoad}
                      />
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default SharePOA;
